import { CNPJ_BASE_LENGTH, CNPJ_LENGTH, CNPJ_UNMASKED_PATTERN } from './constants';

/**
 * Converts a CNPJ character to its numeric value (ASCII code - 48).
 * Digits 0-9 keep their values; letters A-Z map to 17-42.
 */
export function getCnpjCharValue(char: string): number {
  return char.toUpperCase().charCodeAt(0) - 48;
}

export function stripCnpjMask(value: string): string {
  if (!value) {
    return '';
  }

  return value.toUpperCase().replace(/[.\-/]/g, '');
}

export function isValidCnpjFormat(value: string): boolean {
  return CNPJ_UNMASKED_PATTERN.test(stripCnpjMask(value));
}

function calculateCnpjCheckDigit(base: string, startWeight: number): number {
  let sum = 0;
  let weight = startWeight;

  for (let index = 0; index < base.length; index++) {
    sum += getCnpjCharValue(base[index]) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }

  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

export function isValidCnpj(value: string): boolean {
  const cnpj = stripCnpjMask(value);

  if (!isValidCnpjFormat(cnpj)) {
    return false;
  }

  if (/^(.)\1+$/.test(cnpj)) {
    return false;
  }

  const identifiers = cnpj.substring(0, CNPJ_BASE_LENGTH);
  const checkDigits = cnpj.substring(CNPJ_BASE_LENGTH);

  const firstCheckDigit = calculateCnpjCheckDigit(identifiers, 5);
  if (firstCheckDigit !== Number.parseInt(checkDigits[0], 10)) {
    return false;
  }

  const secondCheckDigit = calculateCnpjCheckDigit(identifiers + firstCheckDigit, 6);
  return secondCheckDigit === Number.parseInt(checkDigits[1], 10);
}

export function formatCnpj(value: string): string {
  const cnpj = stripCnpjMask(value);

  if (cnpj.length !== CNPJ_LENGTH) {
    return value;
  }

  return cnpj.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})$/, '$1.$2.$3/$4-$5');
}
